import {ref, reactive, computed, watch, watchEffect, nextTick} from 'vue';
import config from "@/config/config.json";
import {rgbaStr2arr, rgbaArr2argbHex}  from "@/modules/utils/colorUtils";
import {saveDrawHistory, updateFromRemoteHistory, saveNoteHistory, updateSingeNoteHistory, updateAllNoteHistory} from "@/modules/localDB/toolHistory"
import {isTouchDevice} from "@/modules/utils/deviceUtils";
import {usePageResize} from "@/modules/reader/book/pageResize";
import {usePaging} from "@/modules/reader/book/paging";
import {useBookInfo} from "@/modules/reader/book/bookinfo";
import {replaceDefaultTouchBehavior, restoreDefaultTouchBehavior}  from "@/modules/utils/touchUtils";
import * as freehandTool from "@/modules/reader/tools/drawing/freehandTool";
import * as straightLineTool from "@/modules/reader/tools/drawing/straightLineTool";
import * as arrowTool from "@/modules/reader/tools/drawing/arrowTool";
import * as eraserTool from "@/modules/reader/tools/drawing/eraserTool";
import cssvar from '@/scss/_variables.scss';

const noteWidth = parseInt(cssvar.noteWidth);
const noteHeight = parseInt(cssvar.noteHeight);
const {pageResizeState} = usePageResize();
const {pageState} = usePaging();
//const {closeSyncOptions} = useBookInfo();

const toolState = reactive({
    isAllowed:false,
    isShowPanel:false,
    isShowToolbox:false,
    activeTool:null,
    isUsing: false, //tell whether drawing, note etc is in progress. Used to lower the annotation buttons when tool is using.
    brushSetting:{
        size: config.reader.tool.sizes[0],
        color: config.reader.tool.colors.slice(-1).pop(),
        lineMode:config.reader.tool.brushModes.freehand,
        opacity: config.reader.tool.brushOpacity
    },
    highlightSetting: {
        size: config.reader.tool.sizes[0],
        color: config.reader.tool.colors[0],
        opacity: config.reader.tool.highlightOpacity
    },
    eraserSetting: {
        size: config.reader.tool.sizes[0]
    },
    isRestored:false,
    isRightPageUpdated:false, //determine if new drawing is added to the right page. Used to sync draw data when page mode is changed
    noteList:[],
    noteOrdering:[],
    activeNote:null,
    isNoteDisabled:false, //disable if page image width / height is smaller than note width/height
    isSyncProgress:false, //tell whether backup/restore is performing
    canvasScale:computed(()=>{
        //the canvas size is scaled up so that the resolution will be correct when maximum zoom level is reached
        return config.reader.maxZoom  / pageResizeState.imgToBrowserScale
    })
})

const canvasElList = {}
const rightCanvasData = ref([])
const TOOL_BRUSH = "brush";
const TOOL_HIGHLIGHTER = "highlighter";
const TOOL_NOTE = "note";
const TOOL_ERASER = "eraser";

const getCanvas = (id) => {
    return canvasElList[id]
}

const closeToolPanel = ()=> {
    toolState.isShowPanel = false;
}

const closeToolbox = ()=> {
    toolState.isShowToolbox = false;
}

const closeToolBoxAndPanel  = ()=> {
    closeToolPanel();
    closeToolbox();
}

/**
 * Remove the drawings on all canvases, used during restoring.
 */
const clearAllCanvases = ()=> {

    for (let [id,value] of Object.entries(canvasElList)) {
        let ctx = value.getContext("2d");
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0,0, value.width, value.height);
        ctx.restore();
    }
}


export function useTool() {

    const toggleToolAllowed = ()=> {
        toolState.isAllowed  = !toolState.isAllowed;

        if (!toolState.isAllowed ) {
            closeToolBoxAndPanel();
            clearActiveNote();
            restoreDefaultTouchBehavior();
        } else {
            replaceDefaultTouchBehavior();
            if (!toolState.activeTool) {
                toolState.activeTool = TOOL_BRUSH
            }
        }
    }

    const togglePanel = ()=> {
        toolState.isShowPanel  = !toolState.isShowPanel;

    }

    const toggleToolbox = (e)=> {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        toolState.isShowToolbox = !toolState.isShowToolbox;
        useBookInfo().closeSyncOptions();
    }


    const setActiveColor = (color)=> {
        if (toolState.activeTool === TOOL_BRUSH) {
            toolState.brushSetting.color = color;
        } else  if (toolState.activeTool === TOOL_HIGHLIGHTER) {
            toolState.highlightSetting.color = color;
        }
    }

    const setToolSize = (size)=> {
        if (toolState.activeTool === TOOL_BRUSH) {
            toolState.brushSetting.size = size;
        } else  if (toolState.activeTool === TOOL_HIGHLIGHTER) {
            toolState.highlightSetting.size = size;
        } else if (toolState.activeTool === TOOL_ERASER) {
            toolState.eraserSetting.size = size;
        }
    }

    const setBrushMode = (mode)=> {
        toolState.brushSetting.lineMode = mode;
    }

    const initCanvas = (el)=>{
        canvasElList[el.id] = el;
    }

    const restoreBrushHistory = (canvas, history, pagenum) =>{

        let ctx = canvas.getContext("2d");

        if (history.brush) {
            for (let data of history.brush) {
                if (data.isEraser === true) {
                    eraserTool.restoreHistory(ctx, data);
                } else {
                    //freehand
                    if (data.type === config.reader.tool.brushModes.freehand) {
                        freehandTool.restoreHistory(ctx, data);
                        //straight line
                    } else if (data.type === config.reader.tool.brushModes.solidLine) {
                        straightLineTool.restoreHistory(ctx, data);
                        //arrow
                    } else if (data.type === config.reader.tool.brushModes.dashLine) {
                        straightLineTool.restoreHistory(ctx, data, true);
                        //arrow
                    } else if (data.type === config.reader.tool.brushModes.arrow) {
                        arrowTool.restoreHistory(ctx, data);
                    }
                }
            }
        }
    }

    const restoreNoteHistory = (history, pagenum)=> {

        //restore note:
        if (history.note) {
            for (let data of history.note) {
                //if no data.x and data.y value, it should be restoring from remote, since the remote history doesn't contain these properties
                //for backward compatibility for the app version:
                if (data.x === undefined && data.y === undefined) {
                    data.x = data.iconX;
                    data.y = data.iconY;

                    if (!data.title) {
                        data.title = data.name;
                    }

                    data = {
                        x: data.contentX,
                        y: data.contentY,
                        minPosX:data.iconX,
                        minPosY:data.iconY,
                        content: data.content,
                        title: data.title,
                        id:data.id,
                        isMaximized:false
                    }


                    addNote(pagenum, data);
                    //otherwise, it should be restoring from local history:
                } else {

                    //note we always use the min pos!
                    data.x = data.minPosX;
                    data.y = data.minPosY;
                    data.minPosX = data.minPosX;
                    data.minPosY = data.minPosY;

                    //for backward compatibility for the app version...
                    if (!data.title) {
                        data.title = data.name;
                    }

                    addNote(pagenum, data, true);
                }
            }
        }
    }

    /**
     * Save drawing history to local db
     * @param targetPage
     */
    const saveDrawing = async (targetPage, ctx, history) =>{

        //the color value is saved as hex format for backup/restore between apps and web version.
        //We attempt to use hex format to ease the sync work
        let color;
        let type;
        let isEraser = false;

        if (toolState.activeTool === TOOL_BRUSH) {
            type = toolState.brushSetting.lineMode;
            //highlighter tool type value always equals to 1
        }  else if (toolState.activeTool === TOOL_HIGHLIGHTER) {
            type = config.reader.tool.brushModes.solidLine;
            //eraser tool type value always equals to 0
        } else if (toolState.activeTool === TOOL_ERASER) {
            type = config.reader.tool.brushModes.freehand;
            isEraser = true;
        }

        if (isEraser) {
            color = rgbaArr2argbHex("rgba(0,0,0,1)");
        } else {

            //Remark: the html5 canvas strokeStyle property somehow cannot return the rgba string format if opacity is 1.
            //So when opacity is one, pass the toolState.brushSetting.color directly to rgbaStr2arr.
            if (toolState.brushSetting.opacity === 1 && toolState.activeTool === TOOL_BRUSH) {
                color = rgbaArr2argbHex(rgbaStr2arr(toolState.brushSetting.color));
            } else {
                color = rgbaArr2argbHex(rgbaStr2arr(ctx.strokeStyle));
            }

        }

        let drawAttributes = {
            color:color,
            size:ctx.lineWidth / config.reader.maxZoom,
            type:type,
            isEraser:isEraser
        }

        //note that the page stored for backup/restore api is one-based but not zero-based, so we add one:
        await saveDrawHistory(drawAttributes, targetPage + 1, history);
    }

    const addHistory  =(history, x, y) => {
        x = Math.round(x / config.reader.maxZoom * 10000) / 10000;
        y = Math.round(y / config.reader.maxZoom * 10000) / 10000;

        history.push({x: x,y: y});
    }


    const saveLocalFromRemote =  async (remoteHistory) => {
        return await updateFromRemoteHistory(remoteHistory);
    }

    const addNote = (pagenum, meta, isLocalRestore)=> {

        if (!toolState.noteList[pagenum]) {
            toolState.noteList[pagenum] = [];
        }

        if (!toolState.noteOrdering[pagenum]) {
            toolState.noteOrdering[pagenum] = [];
        }

        //generate random note id, just used for identify which note for deletion
        if (!meta.id) {
            meta.id = (+new Date).toString(36);
            meta.zIndex = toolState.noteList[pagenum].length + 1;
            meta.isMaximized = true;
        } else {
            //minimize note if restore from local db
            meta.isMaximized = false;
        }

        toolState.noteList[pagenum].push(meta);
        toolState.noteOrdering[pagenum].push(meta.id);

        if (!isLocalRestore)
        {
            saveNote(pagenum, meta);
        }

    }

    /**
     * Only update the data of the active single note. Used when a single note position is changed or the fields have been updated.
     * @param pagenum
     * @param history
     * @returns {Promise<void>}
     */
    const updateSingleNote = async (pagenum, history) => {
        console.log("@updateSingeNote history:");
        console.log(history)
        await updateSingeNoteHistory(pagenum + 1, history);
    }


    const updateNoteMaximizedState = (pagenum, index, isMaximized) => {
        toolState.noteList[pagenum][index].isMaximized = isMaximized;
    }

    /**
     * Used when deleting note. Since the entire note list will change after a note is deleted, so, just update all notes
     * @param pagenum
     * @returns {Promise<void>}
     */
    const updateAllNotes = async (pagenum) => {
        await updateAllNoteHistory(pagenum + 1, toolState.noteList[pagenum]);
    }

    const setActiveNote = (id, pagenum)=>{

        toolState.activeNote = id;
        let activeIndex = toolState.noteOrdering[pagenum].indexOf(id);
        toolState.noteOrdering[pagenum] = toolState.noteOrdering[pagenum].concat(toolState.noteOrdering[pagenum].splice(activeIndex, 1));

    }

    const clearActiveNote = ()=>{
        toolState.activeNote = null;
        toolState.isUsing = false;
    }

    const deleteNote = async (pagenum,id)=> {

        for (let [index, item] of toolState.noteList[pagenum].entries()) {
            if (item.id === id) {
                toolState.noteList[pagenum].splice(index,1);
                break;
            }
        }

        //after a note is deleted, the entire note list will change, so, just update all notes
        await updateAllNotes(pagenum);
        clearActiveNote();
    }

    /**
     * Store to local history every time note is added, or position / content changed
     */
    const saveNote = async (targetPage, noteInfo)=> {

        console.log("@tool.js savenote:");
        console.log(noteInfo)
        await saveNoteHistory(targetPage + 1, noteInfo);
    }

    /**
     * Remove existing notes when restoring from remote
     */
    const clearAllNotes = ()=>{
        toolState.noteList = [];
        toolState.noteOrdering = [];
        toolState.activeNote = null;
    }

    /**
     * If page width /height is not enough to fit in note, disable the note function
     */
    const setIsNoteDisabled = ()=>{
        if (pageState.isRightPageAvailable) {
            toolState.isNoteDisabled = false;
        } else {
            if (pageResizeState.fittedBrowserWidth < noteWidth || pageResizeState.fittedBrowserHeight < noteHeight) {
                toolState.isNoteDisabled = true;
            } else {
                toolState.isNoteDisabled = false;
            }
        }
    }

    const reset = () => {
        console.log("reset @tools.js")
        toolState.isAllowed = false;
        toolState.isShowPanel = false;
        toolState.isShowToolbox = false;
        toolState.isUsing = false;
        toolState.activeTool = null;
        toolState.brushSetting = {
            size:  config.reader.tool.sizes[0],
            color: config.reader.tool.colors.slice(-1).pop(),
            lineMode:config.reader.tool.brushModes.freehand,
            opacity: config.reader.tool.brushOpacity
        };
        toolState.highlightSetting = {
            size: config.reader.tool.sizes[0],
            color: config.reader.tool.colors[0],
            opacity: config.reader.tool.highlightOpacity
        };
        toolState.eraserSetting = {
            size: config.reader.tool.sizes[0]
        }
        toolState.isRestored = false;
        toolState.noteList = [];
        toolState.noteOrdering = [];
        toolState.isNoteDisabled = false;
        toolState.activeNote = null;
        toolState.isSyncProgress = false;
    }

    /*
    watchEffect(()=>{
        if (toolState.activeTool !== TOOL_NOTE || toolState.isAllowed) {
            clearActiveNote();
            document.getElementById("debug").innerHTML += "yaaaaa<br>"
        }
    })*/


    return {
        toolState,
        initCanvas,
        getCanvas,
        clearAllCanvases,
        rightCanvasData,
        closeToolPanel,
        closeToolBoxAndPanel,
        toggleToolAllowed,
        togglePanel,
        toggleToolbox,
        setActiveColor,
        setToolSize,
        setBrushMode,
        restoreBrushHistory,
        restoreNoteHistory,
        saveDrawing,
        saveLocalFromRemote,
        addHistory,
        addNote,
        deleteNote,
        setActiveNote,
        clearActiveNote,
        updateSingleNote,
        clearAllNotes,
        updateNoteMaximizedState,
        setIsNoteDisabled,
        reset,
        TOOL_BRUSH,
        TOOL_HIGHLIGHTER,
        TOOL_NOTE,
        TOOL_ERASER
    }
}

//TODO: try cleaning... usage: useTool().methodNmae() to call these instead...
export {toolState,getCanvas,rightCanvasData, closeToolPanel}
